import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Trophy, Download, RefreshCcw, Filter, AlertTriangle,
  HandCoins, Activity, CheckCircle2, UserPlus, ArrowUpDown
} from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtINR(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

function formatDaysAgo(d) {
  if (!d) return 'never';
  const diff = (Date.now() - new Date(d).getTime()) / 86400000;
  if (diff < 1) return 'today';
  if (diff < 2) return 'yesterday';
  return `${Math.floor(diff)}d ago`;
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];

function presetRange(key) {
  const now = new Date();
  if (key === 'today') return { from: ymd(now), to: ymd(now) };
  if (key === 'yesterday') {
    const y = new Date(now); y.setDate(now.getDate() - 1);
    return { from: ymd(y), to: ymd(y) };
  }
  if (key === 'week') {
    const day = now.getDay() || 7;
    const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
    return { from: ymd(monday), to: ymd(now) };
  }
  if (key === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(first), to: ymd(now) };
  }
  if (key === 'last30') {
    const from = new Date(now); from.setDate(now.getDate() - 29);
    return { from: ymd(from), to: ymd(now) };
  }
  return { from: ymd(now), to: ymd(now) };
}

// Sort columns
const COLS = [
  { key: 'total', label: 'Score' },
  { key: 'paymentsAmount', label: '₹ Collected' },
  { key: 'paymentsCount', label: 'Payments' },
  { key: 'visits', label: 'Visits' },
  { key: 'registers', label: 'New Leads' },
  { key: 'onboards', label: 'Onboarded' },
  { key: 'reschedules', label: 'Resched.' },
  { key: 'daysInactive', label: 'Idle (days)' },
];

export default function OwnerTeam360() {
  const navigate = useNavigate();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const isManager = currentUser.role === 'manager';

  const [preset, setPreset] = useState('week');
  const [range, setRange] = useState(presetRange('week'));
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [showIdle, setShowIdle] = useState(true);
  const [sortBy, setSortBy] = useState('paymentsAmount');
  const [sortDir, setSortDir] = useState('desc');
  const [fieldData, setFieldData] = useState({ summary: { byAgent: [] } });
  const [payData, setPayData] = useState({ summary: { byAgent: [] } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(presetRange(key));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams = { limit: 5000 };
      if (range.from) baseParams.from = range.from;
      if (range.to) baseParams.to = range.to;
      if (branchId) baseParams.branchId = branchId;
      const [fa, pay] = await Promise.all([
        api.get('/reports/field-activity', { params: baseParams }).catch(() => ({ data: { summary: { byAgent: [] } } })),
        api.get('/recovery/payments', { params: baseParams }).catch(() => ({ data: { summary: { byAgent: [] } } })),
      ]);
      setFieldData(fa.data || { summary: { byAgent: [] } });
      setPayData(pay.data || { summary: { byAgent: [] } });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, branchId]);

  useEffect(() => { load(); }, [load]);

  // Merge by agentId
  const rows = useMemo(() => {
    const byId = new Map();

    // Seed from field-activity (gives us the canonical agent list incl. idle)
    for (const a of fieldData?.summary?.byAgent || []) {
      byId.set(a.agentId, {
        agentId: a.agentId,
        name: a.name,
        employeeId: a.employeeId,
        branchName: a.branchName,
        isActive: a.isActive !== false,
        lastActiveAt: a.lastActiveAt,
        daysInactive: a.daysInactive,
        visits: a.visits || 0,
        reschedules: a.reschedules || 0,
        registers: a.registers || 0,
        onboards: a.onboards || 0,
        uniqueMerchants: a.uniqueMerchants || 0,
        paymentsAmount: 0,
        paymentsCount: 0,
      });
    }

    // Merge in collections
    for (const a of payData?.summary?.byAgent || []) {
      const existing = byId.get(a.userId);
      if (existing) {
        existing.paymentsAmount = a.amount || 0;
        existing.paymentsCount = a.count || 0;
      } else {
        // Agent collected but had no merchant-side activity (or isn't in field scope) — still show them
        byId.set(a.userId, {
          agentId: a.userId,
          name: a.name,
          employeeId: a.employeeId,
          branchName: null,
          isActive: true,
          lastActiveAt: null,
          daysInactive: null,
          visits: 0, reschedules: 0, registers: 0, onboards: 0, uniqueMerchants: 0,
          paymentsAmount: a.amount || 0,
          paymentsCount: a.count || 0,
        });
      }
    }

    // Compute a simple composite score (transparent so the user can audit)
    // Score = visits*2 + registers*3 + onboards*5 + reschedules*1 + paymentsCount*4 + log10(paymentsAmount)*10
    const scored = Array.from(byId.values()).map(r => {
      const cashScore = r.paymentsAmount > 0 ? Math.log10(r.paymentsAmount + 1) * 10 : 0;
      r.total = Math.round(r.visits * 2 + r.registers * 3 + r.onboards * 5 + r.reschedules * 1 + r.paymentsCount * 4 + cashScore);
      r.fieldActions = r.visits + r.reschedules + r.registers + r.onboards;
      return r;
    });

    return scored;
  }, [fieldData, payData]);

  // Filter + search + sort
  const visibleRows = useMemo(() => {
    let arr = rows;
    if (!showIdle) arr = arr.filter(r => r.fieldActions > 0 || r.paymentsCount > 0);
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      arr = arr.filter(r => `${r.name || ''} ${r.employeeId || ''} ${r.branchName || ''}`.toLowerCase().includes(q));
    }
    arr = [...arr].sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (sortBy === 'daysInactive') {
        // null = never logged in = treat as Infinity (worst)
        av = av == null ? Infinity : av;
        bv = bv == null ? Infinity : bv;
      } else {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      }
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return arr;
  }, [rows, showIdle, searchQ, sortBy, sortDir]);

  // KPI tiles
  const totals = useMemo(() => {
    const t = { visits: 0, registers: 0, onboards: 0, reschedules: 0, paymentsAmount: 0, paymentsCount: 0 };
    rows.forEach(r => {
      t.visits += r.visits; t.registers += r.registers; t.onboards += r.onboards; t.reschedules += r.reschedules;
      t.paymentsAmount += r.paymentsAmount; t.paymentsCount += r.paymentsCount;
    });
    const active = rows.filter(r => r.fieldActions > 0 || r.paymentsCount > 0).length;
    const idle = rows.filter(r => r.isActive && r.fieldActions === 0 && r.paymentsCount === 0).length;
    const stale = rows.filter(r => r.daysInactive != null && r.daysInactive >= 3).length;
    return { ...t, active, idle, stale, total: rows.length };
  }, [rows]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const downloadCsv = () => {
    const lines = [['#', 'Agent', 'Employee ID', 'Branch', 'Score', 'Visits', 'Reschedules', 'New Leads', 'Onboarded', 'Unique Merchants', 'Payments', 'Amount Collected', 'Last Active', 'Days Idle']];
    visibleRows.forEach((r, i) => {
      lines.push([
        i + 1,
        r.name || '',
        r.employeeId || '',
        r.branchName || '',
        r.total,
        r.visits, r.reschedules, r.registers, r.onboards, r.uniqueMerchants,
        r.paymentsCount, r.paymentsAmount.toFixed(2),
        r.lastActiveAt ? new Date(r.lastActiveAt).toISOString() : '',
        r.daysInactive == null ? 'never' : r.daysInactive,
      ]);
    });
    const csv = lines.map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team_360_${range.from || 'all'}_${range.to || 'all'}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <OwnerLayout title="Team 360">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Team 360</h2>
          <p className="text-sm text-gray">Combined field activity + collections per agent — one row per person</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-outline" onClick={load} title="Refresh">
            <RefreshCcw size={14} style={{ marginRight: '4px' }} /> Refresh
          </button>
          <button className="btn-navy" onClick={downloadCsv} disabled={!visibleRows.length}>
            <Download size={14} style={{ marginRight: '4px' }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="panel" style={{ padding: '12px 16px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--g3)', fontWeight: 600 }}><Filter size={12} /> Range:</span>
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => applyPreset(p.key)}
              className={preset === p.key ? 'btn-navy' : 'btn-outline'}
              style={{ padding: '4px 10px', fontSize: '12px', minWidth: 'auto' }}
              data-testid={`t360-preset-${p.key}`}>
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input type="date" className="form-input"
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
                value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
              <span>→</span>
              <input type="date" className="form-input"
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
                value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            </>
          )}
          <div style={{ width: '1px', height: '20px', background: 'var(--g2)', margin: '0 4px' }} />
          {branches.length > 1 && !isManager && (
            <select className="form-input" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
          <input type="text" className="form-input"
            placeholder="Search agent name or ID…"
            style={{ flex: 1, minWidth: '160px', fontSize: '12px', padding: '4px 8px' }}
            value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
          <label style={{ fontSize: '11.5px', color: 'var(--g3)', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} />
            Show idle agents
          </label>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="od-stats">
        <div className="od-stat" style={{ borderLeft: '3px solid var(--navy)' }}>
          <div className="od-stat-lbl"><Users size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Team Size</div>
          <div className="od-stat-val">{totals.total}</div>
          <div className="od-stat-sub">{totals.active} active, {totals.idle} idle</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl"><HandCoins size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Collected</div>
          <div className="od-stat-val" style={{ color: '#16a34a' }}>{fmtINR(totals.paymentsAmount)}</div>
          <div className="od-stat-sub">{totals.paymentsCount} payment{totals.paymentsCount !== 1 ? 's' : ''}</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl"><CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Field Work</div>
          <div className="od-stat-val">{totals.visits}</div>
          <div className="od-stat-sub">visits · {totals.registers} new · {totals.onboards} onboarded</div>
        </div>
        <div className="od-stat" style={{ borderLeft: totals.stale > 0 ? '3px solid var(--red)' : '3px solid var(--g2)' }}>
          <div className="od-stat-lbl"><AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Not Logging In</div>
          <div className="od-stat-val" style={{ color: totals.stale > 0 ? 'var(--red)' : 'var(--g3)' }}>{totals.stale}</div>
          <div className="od-stat-sub">≥ 3 days since last login</div>
        </div>
      </div>

      {/* Combined leaderboard */}
      <div className="panel" style={{ marginTop: '12px', marginBottom: '24px' }}>
        <div className="panel-header">
          <span className="panel-title"><Trophy size={14} /> Agent Scorecard ({visibleRows.length})</span>
        </div>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
            No agents to show. Try a wider date range or toggle "Show idle agents".
          </div>
        ) : (
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
          <thead>
            <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)', width: '40px' }}>#</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Agent</th>
              {COLS.map(c => (
                <th key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{
                    textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600,
                    color: sortBy === c.key ? 'var(--navy)' : 'var(--g3)',
                    cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
                  }}>
                  {c.label}
                  {sortBy === c.key && <ArrowUpDown size={10} style={{ verticalAlign: 'middle', marginLeft: 3 }} />}
                </th>
              ))}
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) => {
              const isStale = r.daysInactive != null && r.daysInactive >= 3;
              const isIdle = r.fieldActions === 0 && r.paymentsCount === 0;
              const showWarning = isIdle && r.isActive;
              return (
                <tr key={r.agentId}
                    style={{ borderBottom: '1px solid var(--g2)', opacity: isIdle ? 0.7 : 1, background: showWarning ? 'rgba(254,202,202,0.18)' : undefined }}
                    data-testid={`t360-row-${r.agentId}`}>
                  <td style={{ padding: '8px 12px', color: 'var(--g3)' }}>{i + 1}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>
                      {r.name}{r.employeeId ? ` (${r.employeeId})` : ''}
                      {showWarning && <AlertTriangle size={11} style={{ marginLeft: 6, color: 'var(--red)', verticalAlign: 'middle' }} />}
                    </div>
                    <div style={{ fontSize: '10.5px', color: 'var(--g3)' }}>{r.branchName || '—'}</div>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <strong style={{ color: r.total > 0 ? 'var(--navy)' : 'var(--g3)' }}>{r.total}</strong>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {r.paymentsAmount > 0
                      ? <span style={{ color: '#16a34a', cursor: 'pointer', fontWeight: 600 }} title="Open in Activity"
                          onClick={() => navigate(`/owner/recovery/activity?type=payment&agentId=${r.agentId}&from=${range.from}&to=${range.to}`)}>
                          {fmtINR(r.paymentsAmount)}
                        </span>
                      : <span style={{ color: 'var(--g3)' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: r.paymentsCount > 0 ? 'var(--g2)' : 'var(--g3)' }}>{r.paymentsCount || '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {r.visits > 0
                      ? <span style={{ color: '#16a34a', cursor: 'pointer', fontWeight: 600 }} title="Open in Team Activity"
                          onClick={() => navigate(`/owner/team-activity?agentId=${r.agentId}&from=${range.from}&to=${range.to}&type=visit`)}>{r.visits}</span>
                      : <span style={{ color: 'var(--g3)' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: r.registers > 0 ? '#1d4ed8' : 'var(--g3)' }}>{r.registers || '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: r.onboards > 0 ? '#7c3aed' : 'var(--g3)' }}>{r.onboards || '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: r.reschedules > 0 ? '#b45309' : 'var(--g3)' }}>{r.reschedules || '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {r.daysInactive == null
                      ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>never</span>
                      : <span style={{ color: isStale ? 'var(--red)' : 'var(--g3)', fontWeight: isStale ? 600 : 400 }}>{r.daysInactive}</span>}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: '11.5px' }}>
                    <span style={{ color: isStale ? 'var(--red)' : 'var(--g3)' }}>
                      {formatDaysAgo(r.lastActiveAt)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        )}
        <div style={{ padding: '10px 16px', fontSize: '10.5px', color: 'var(--g3)', borderTop: '1px solid var(--g2)' }}>
          <strong>Score</strong> = visits×2 + new leads×3 + onboarded×5 + reschedules×1 + payments×4 + log(₹+1)×10.
          Tap a number to drill into the underlying report.
        </div>
      </div>
    </OwnerLayout>
  );
}
